import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Button, Loading, MessageBanner, TextArea, } from "@folio/stripes/components";
import { FormattedMessage, useIntl } from "react-intl";
import { useOkapiKy } from "@folio/stripes/core";
import { AgendaChangeModal } from "./AgendaChangeModal";
import { AgendaHandlerChoice } from "./AgendaHandlerChoice";
import { ChangeAgendaDialog } from "./ChangeAgendaDialog";
import { MarkMonographicIssueWizard } from "./MarkMonographicIssueWizard";
import { leaveIssueLink, setIssueAgenda, unlinkIssueFromMonograph, } from "../../api/agendaService";
import { useIssueAgenda } from "../../hooks/useAgenda";
import { otherAgenda, } from "../../types/agenda";
import { ROUTE_PATHS } from "../../types/routePaths";
import { formatDateTime } from "../../utils/formatUtils";
import { formatIsbn } from "../../utils/isbn";
import { isForbidden } from "../../utils/apiUtils";
import css from "./Agenda.module.css";
var monographDetailPath = function (titleId, isbn) {
    return "".concat(isbn ? ROUTE_PATHS.TITLES_WITH_ISBN : ROUTE_PATHS.TITLES_WITHOUT_ISBN, "?detail=").concat(titleId);
};
// #76093: an issue reaches the monographs only through a monograph title; an older own decision can still be handed back
var canHandOwnDecisionBack = function (agenda) {
    return (agenda.canDecideOnItsOwn || agenda.overridden) &&
        (agenda.agenda === "MONOGRAPHS" || agenda.overridden);
};
export var IssueAgendaSection = function (_a) {
    var _b, _c, _d;
    var issueId = _a.issueId, issueLabel = _a.issueLabel;
    var formatMessage = useIntl().formatMessage;
    var kyInstance = useOkapiKy();
    var agendaQuery = useIssueAgenda(issueId);
    var issueAgenda = agendaQuery.data;
    var link = (_b = issueAgenda === null || issueAgenda === void 0 ? void 0 : issueAgenda.specialIssueLink) !== null && _b !== void 0 ? _b : null;
    var _e = useState(null), action = _e[0], setAction = _e[1];
    var _f = useState(false), ownAgendaOpen = _f[0], setOwnAgendaOpen = _f[1];
    var _g = useState(false), wizardOpen = _g[0], setWizardOpen = _g[1];
    var _h = useState(null), handler = _h[0], setHandler = _h[1];
    var _j = useState(""), note = _j[0], setNote = _j[1];
    useEffect(function () {
        if (action === null) {
            setHandler(null);
            setNote("");
        }
    }, [action]);
    var openAction = useCallback(function (next) { return function () {
        setAction(next);
        if (next === "handOver" && issueAgenda) {
            setHandler(otherAgenda(issueAgenda.agenda));
        }
    }; }, [issueAgenda]);
    var closeAction = useCallback(function () { return setAction(null); }, []);
    var openOwnAgenda = useCallback(function () { return setOwnAgendaOpen(true); }, []);
    var closeOwnAgenda = useCallback(function () { return setOwnAgendaOpen(false); }, []);
    var openWizard = useCallback(function () { return setWizardOpen(true); }, []);
    var closeWizard = useCallback(function () { return setWizardOpen(false); }, []);
    var noteValue = note.trim() || undefined;
    var needsHandler = action === "join" || action === "handOver";
    var canSubmit = action !== null && (!needsHandler || handler !== null);
    var run = useCallback(function (dryRun) {
        if (action === "leave") {
            return leaveIssueLink(kyInstance, issueId, noteValue, dryRun);
        }
        if (action === "unlink") {
            return unlinkIssueFromMonograph(kyInstance, issueId, noteValue, dryRun);
        }
        if (!handler) {
            return Promise.reject(new Error("The issue handler must be chosen before the change"));
        }
        return setIssueAgenda(kyInstance, issueId, { agenda: handler, note: noteValue }, dryRun);
    }, [action, kyInstance, issueId, handler, noteValue]);
    var previewKey = useMemo(function () { return ["issue", issueId, action, handler]; }, [issueId, action, handler]);
    var handleNoteChange = useCallback(function (e) { return setNote(e.target.value); }, []);
    var dialogKind = action === "handOver" ? "join" : (action !== null && action !== void 0 ? action : "join");
    return (React.createElement("div", { className: css.section, "data-test-id": "issue-agenda-section" },
        React.createElement("p", { className: css.statusLine },
            React.createElement("strong", null,
                React.createElement(FormattedMessage, { id: "ui-br-legaldeposits.agenda.issue.heading" }),
                ": "),
            issueAgenda && !link && (React.createElement("span", { "data-test-id": "issue-agenda-own-state" },
                React.createElement(FormattedMessage, { id: issueAgenda.overridden && issueAgenda.agenda === "MONOGRAPHS"
                        ? "ui-br-legaldeposits.agenda.issue.state.ownMonographs"
                        : "ui-br-legaldeposits.agenda.issue.notLinked" }),
                !issueAgenda.canDecideOnItsOwn && (React.createElement(React.Fragment, null,
                    " ",
                    React.createElement("span", { className: css.hint },
                        React.createElement(FormattedMessage, { id: "ui-br-legaldeposits.agenda.issue.merged" })))))),
            issueAgenda && link && (React.createElement(React.Fragment, null,
                React.createElement(FormattedMessage, { id: "ui-br-legaldeposits.agenda.issue.linkedTo" }),
                " ",
                React.createElement(Link, { to: monographDetailPath(link.monographTitleId, link.monographIsbn) }, (_c = link.monographTitle) !== null && _c !== void 0 ? _c : link.monographTitleId),
                link.monographIsbn && " (".concat(formatIsbn(link.monographIsbn), ")"),
                " ",
                React.createElement("span", { className: css.hint },
                    React.createElement(FormattedMessage, { id: "ui-br-legaldeposits.agenda.issue.linkedBy", values: {
                            who: (_d = link.createdByName) !== null && _d !== void 0 ? _d : "–",
                            when: formatDateTime(link.createdAt),
                        } })),
                " ",
                React.createElement("span", { "data-test-id": "issue-agenda-state" }, issueAgenda.joinedLink ? (React.createElement(FormattedMessage, { id: "ui-br-legaldeposits.agenda.issue.state.joined", values: { handler: issueAgenda.agenda } })) : (React.createElement(FormattedMessage, { id: "ui-br-legaldeposits.agenda.issue.state.notJoined" })))))),
        agendaQuery.isLoading && React.createElement(Loading, null),
        agendaQuery.isError && (React.createElement(MessageBanner, { type: "error" },
            React.createElement(FormattedMessage, { id: isForbidden(agendaQuery.error)
                    ? "ui-br-legaldeposits.agenda.issue.forbidden"
                    : "ui-br-legaldeposits.agenda.issue.loadFailed" }))),
        issueAgenda &&
            !link &&
            (issueAgenda.canDecideOnItsOwn ||
                canHandOwnDecisionBack(issueAgenda)) && (React.createElement("div", { className: css.actions },
            issueAgenda.canDecideOnItsOwn && (React.createElement(Button, { buttonStyle: "primary", onClick: openWizard, marginBottom0: true, "data-test-id": "issue-agenda-mark-monographic" },
                React.createElement(FormattedMessage, { id: "ui-br-legaldeposits.agenda.issue.action.markMonographic" }))),
            canHandOwnDecisionBack(issueAgenda) && (React.createElement(Button, { onClick: openOwnAgenda, marginBottom0: true, "data-test-id": "issue-agenda-own" },
                React.createElement(FormattedMessage, { id: issueAgenda.agenda === "MONOGRAPHS"
                        ? "ui-br-legaldeposits.agenda.action.issueToPeriodicals"
                        : "ui-br-legaldeposits.agenda.action.issueToMonographs" }))))),
        issueAgenda && link && (React.createElement("div", { className: css.actions },
            issueAgenda.joinedLink ? (React.createElement(React.Fragment, null,
                React.createElement(Button, { onClick: openAction("handOver"), marginBottom0: true, "data-test-id": "issue-agenda-hand-over" },
                    React.createElement(FormattedMessage, { id: "ui-br-legaldeposits.agenda.issue.action.handOver.".concat(otherAgenda(issueAgenda.agenda)) })),
                React.createElement(Button, { onClick: openAction("leave"), marginBottom0: true },
                    React.createElement(FormattedMessage, { id: "ui-br-legaldeposits.agenda.issue.action.leave" })))) : (React.createElement(Button, { onClick: openAction("join"), marginBottom0: true, "data-test-id": "issue-agenda-join" },
                React.createElement(FormattedMessage, { id: "ui-br-legaldeposits.agenda.issue.action.join" }))),
            React.createElement(Button, { onClick: openAction("unlink"), marginBottom0: true },
                React.createElement(FormattedMessage, { id: "ui-br-legaldeposits.agenda.issue.action.unlink" })))),
        React.createElement(ChangeAgendaDialog, { open: ownAgendaOpen, mode: "issue", name: issueLabel, titleAgenda: null, issueAgenda: issueAgenda !== null && issueAgenda !== void 0 ? issueAgenda : null, onClose: closeOwnAgenda, onChanged: closeOwnAgenda }),
        React.createElement(MarkMonographicIssueWizard, { open: wizardOpen, issueId: issueId, issueLabel: issueLabel, onClose: closeWizard }),
        React.createElement(AgendaChangeModal, { open: action !== null, heading: formatMessage({
                id: "ui-br-legaldeposits.agenda.issue.dialog.".concat(dialogKind, ".heading"),
            }, { issue: issueLabel }), previewKey: previewKey, canSubmit: canSubmit, run: run, onClose: closeAction, onChanged: closeAction, impactScope: "linkedPair" },
            React.createElement("p", null,
                React.createElement(FormattedMessage, { id: "ui-br-legaldeposits.agenda.issue.dialog.".concat(dialogKind, ".intro") })),
            needsHandler && (React.createElement(AgendaHandlerChoice, { name: "issue-agenda-handler", side: "periodicals", value: handler, onChange: setHandler })),
            React.createElement(TextArea, { label: formatMessage({
                    id: "ui-br-legaldeposits.agenda.dialog.note.label",
                }), value: note, onChange: handleNoteChange }))));
};
